import { Text } from '@user-portal/wikr-components';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { setCancellationCandidates } from 'store/subscriptions/actions';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { sendAnalyticCancelOfferClick, trackScreenLoadCancelOffer } from 'services/Analytics';

import { useSelector } from 'hooks';
import useModals from 'modals/hooks';
import { ModalName } from 'modals/types';

import { getValuesByKey, roundToNearest } from 'helpers/utils';

import OutlinedButton from 'components/Buttons/OutlinedButton';
import Modal from 'components/Modal';

import { CancelFlowOffers, CancelOfferAction } from 'types/subscription';

import SubscriptionsSkeleton from '../../../../components/Skeleton/Skeleton';
import useCancelSubscriptionFlow from '../../../../hooks/useCancelSubscriptionFlow';
import useDiscountModal from '../../../../hooks/useDiscountModal';
import FullScreenModalHeader from '../../../components/FullScreenModalHeader';
import SubscriptionCards from '../components/SubscriptionCards';

import * as S from './styled';

const DiscountModal = () => {
    const { closeModal, openModal } = useModals();
    const { t } = useTranslation();

    const { onCancelSubscription, onChangeSubscription } = useCancelSubscriptionFlow();

    const dispatch = useDispatch();

    const cancellationCandidates = useSelector(selectCancellationCandidates);

    const { currentSubscription, currencySign, discountOffer } = useDiscountModal();

    const isDiscountOfferReady = discountOffer && currentSubscription && currencySign;

    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const onBackButtonClick = () => {
        sendAnalyticCancelOfferClick({
            eventLabel: { subscription_id, offer: CancelFlowOffers.Discount },
            actionEvent: CancelOfferAction.Close,
        });
        openModal(ModalName.CancellationReasonModal);
    };

    useEffect(() => {
        trackScreenLoadCancelOffer({ eventLabel: { subscription_id, offer: CancelFlowOffers.Discount } });
    }, []);

    const sendAnalytics = useCallback(
        (action: CancelOfferAction) => {
            sendAnalyticCancelOfferClick({
                eventLabel: { subscription_id, offer: CancelFlowOffers.Discount },
                actionEvent: action,
            });
        },
        [subscription_id]
    );

    const changeSubscription = useCallback(() => {
        sendAnalytics(CancelOfferAction.Accept);
        onChangeSubscription();
    }, [onChangeSubscription, sendAnalytics]);

    const onClose = useCallback(() => {
        dispatch(setCancellationCandidates(null));
        sendAnalytics(CancelOfferAction.Close);
        closeModal();
    }, [closeModal, sendAnalytics, dispatch]);

    const discount = isDiscountOfferReady
        ? roundToNearest(Math.round(100 * (1 - Number(discountOffer.price) / Number(currentSubscription.price))))
        : null;

    const handleCancelSubscription = useCallback(() => {
        sendAnalytics(CancelOfferAction.Cancel);
        onCancelSubscription();
    }, [onCancelSubscription, sendAnalytics]);

    return (
        <Modal
            fullscreen
            isOpen
            onClose={onClose}
            customStyles={{ background: '#F3F3F8', padding: 0 }}
            withRoundedCorners={false}
            dataLocator="discountOfferContainer"
        >
            <FullScreenModalHeader onBackButtonClick={onBackButtonClick} closeModal={onClose} />

            <div className="container-sm">
                <S.ModalContent>
                    {!isDiscountOfferReady ? (
                        <SubscriptionsSkeleton />
                    ) : (
                        <>
                            <div>
                                <Text medium center mb={12} type="h4" dataLocator="unsubscribeTitle">
                                    {t('subscription.discountOfferModal.title', { percent: discount })}
                                </Text>
                                <Text center type="large-text" dataLocator="unsubscribeDescription">
                                    {t('subscription.discountOfferModal.description', {
                                        percent: discount,
                                    })}
                                </Text>
                            </div>
                            <SubscriptionCards
                                currencySign={currencySign}
                                currentSubscription={currentSubscription}
                                onChangeSubscription={changeSubscription}
                                discountPrice={discountOffer.price}
                                discount={discount as number}
                                title={t('subscription.discountOfferModal.currentCard.title')}
                            />
                            <OutlinedButton
                                onClick={handleCancelSubscription}
                                text={t('basics.button.cancelSubscription')}
                                backgroundColor="surface-main"
                                dataLocator="unsubscribeCancelSubscriptionButton"
                            />
                        </>
                    )}
                </S.ModalContent>
            </div>
        </Modal>
    );
};

export default DiscountModal;
